import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { RadioGroup, Radio, FormControl } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import LabFilingDetails from "./LabFilingDetails";

import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from "@mui/material";
import {
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTableOne,
  fetchTableTwo,
  fetchTableThree,
  fetchDayData,
  fetchDayByCategory,
  setLabReport,
  fetchFiling,
  setSelectedDate,
  setSelectedDay,
} from "../reducers/dashFrameSlice";

const COLORS = {
  filed: "#81c784",
  reviewed_and_actioned: "#ffb74d",
  not_filed_inconclusive: "#e57373",
  not_filed_setup: "#64b5f6",
  not_filed_already: "#ba68c8",
  error: "#ff5252",
};

const BCOLORS = {
  filed: "#388e3c",
  reviewed_and_actioned: "#f57c00",
  not_filed_inconclusive: "#c62828",
  not_filed_setup: "#1565c0",
  not_filed_already: "#6a1b9a",
  error: "#d32f2f",
};

const COLOR_TITLES = {
  filed: "Filed",
  reviewed_and_actioned: "Reviewed and Actioned",
  not_filed_inconclusive: "Not Filed (Inconclusive Report)",
  not_filed_setup: "Category Not Setup",
  not_filed_already: "Not Filed (Already filed by clinician)",
  error: "Error",
};

const TABLE_THREE_COLUMNS = [
  {
    field: "output_date",
    headerName: "Date",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "pathology_enabled",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total Enabled <br /> Path Cat
      </div>
    ),
  },
  {
    field: "enabled_received",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Enabled Path <br /> Reports Recvd
      </div>
    ),
  },
  {
    field: "filed",
    flex: 1,
    cellClassName: "bg-green",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total <br /> Filed Normal
      </div>
    ),
  },
  {
    field: "per_filed",
    flex: 1,
    cellClassName: "bg-green",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        % Total <br /> Filed Normal
      </div>
    ),
  },
  {
    field: "filed_abnormal",
    flex: 1,
    cellClassName: "bg-green",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total <br /> Filed Abnormal
      </div>
    ),
  },
  {
    field: "per_filed_abnormal",
    flex: 1,
    cellClassName: "bg-green",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        % Total <br /> Filed Abnormal
      </div>
    ),
  },
  {
    field: "reviewed_and_actioned",
    flex: 1,
    cellClassName: "bg-orange",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Reviewed and <br /> Actioned
      </div>
    ),
  },
  {
    field: "percentage_reviewed_and_actioned",
    flex: 1,
    cellClassName: "bg-orange",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        % Reviewed and <br /> Actioned
      </div>
    ),
  },
  {
    field: "filed_by_clinician",
    flex: 1,
    cellClassName: "bg-pink",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Already Filed <br /> by GP
      </div>
    ),
  },
  {
    field: "not_setup_count",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Path Cat Recvd <br /> Not Enabled
      </div>
    ),
  },
  {
    field: "inreview",
    flex: 1,
    align: "center",
    headerAlign: "center",
    headerName: "In Review",
  },
];

const TABLE_DAYDATA_COLUMNS = [
  {
    field: "emis_id",
    headerName: "EMIS ID",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "lab_report_id",
    headerName: "Lab Report Id",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "category",
    headerName: "Category",
    flex: 2,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "category_type",
    headerName: "Report Type",
    flex: 2,
    align: "center",
    headerAlign: "center",
    renderCell: (params) =>
      params.value ? "Normal Report" : "Abnormal Report",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "starttime",
    headerName: "Start Time",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "notes",
    headerName: "Notes",
    flex: 4,
    align: "center",
    headerAlign: "center",
  },
];

const PIE_COLORS = [
  "#81c784",
  "#4db6ac",
  "#ffd54f",
  "#9575cd",
  "#7986cb",
  "#a1887f",
  "#f06292",
  "#64b5f6",
  "#e57373",
  "#ba68c8",
];

const DashboardFrame = () => {
  const dispatch = useDispatch();
  const {
    liverun,
    tableOne,
    tableOneTotalProcessed,
    tableOneTotalNormal,
    tableOneTotalAbnormal,
    tableOneInreviewCategories,
    tableOneEnabledCategories,
    tableOneMonthlyTrend,
    lab,
    tableTwo,
    tableThree,
    tableDayByCategory,
    tableDayData,
    selectedDate,
    selectedDay,
    loadingOne,
    loadingTwo,
    loadingThree,
    loadingDayByCategory,
    loadingDayData,
    loadingLabReport,
  } = useSelector((state) => state.dashFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const is_admin = useSelector((state) => state.authen.loggedInUser?.is_admin);

  const TABLE_CATEGORY_COLUMNS = [
    {
      field: "category",
      headerName: "Category",
      flex: 3,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "filed",
      headerName: "Filed",
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-green",
    },
    {
      field: "reviewed_and_actioned",
      headerName: "Reviewed and Actioned",
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-green",
    },
    {
      field: "not_filed_inconclusive",
      headerName: "Not Filed (Inconclusive Report)",
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-orange",
    },
    {
      field: "not_filed_setup",
      headerName: "Category Not Setup",
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-orange",
    },
    {
      field: "not_filed_already",
      headerName: "Not Filed (Already filed by clinician)",
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-pink",
    },
    ...(is_admin
      ? [
          {
            field: "error",
            headerName: "Error",
            flex: 1,
            align: "center",
            headerAlign: "center",
            cellClassName: "bg-red",
          },
        ]
      : []),
  ];
  const [visibleKeys, setVisibleKeys] = useState({
    filed: true,
    reviewed_and_actioned: true,
    not_filed_inconclusive: false,
    not_filed_setup: false,
    not_filed_already: false,
    error: false,
  });

  const [openFilingDialog, setOpenFilingDialog] = useState(false);

  const [page, setPage] = useState(0);
  const [page3, setPage3] = useState(0);
  const [page4, setPage4] = useState(0);
  const [selectedChartType, setSelectedChartType] = useState("filed");

  const pageSize = 10;
  const totalPages = Math.ceil(tableTwo.length / pageSize);

  const paginatedData = tableTwo
    .filter((entry) => entry.filed > 0)
    .sort((a, b) => b.filed - a.filed)
    .slice(page * pageSize, (page + 1) * pageSize);

  const paginatedData3 = tableTwo
    .filter((entry) => entry.not_filed_inconclusive > 0)
    .sort((a, b) => b.not_filed_inconclusive - a.not_filed_inconclusive)
    .slice(page3 * pageSize, (page3 + 1) * pageSize);

  const paginatedData4 = tableTwo
    .filter((entry) => entry.not_filed_setup > 0)
    .sort((a, b) => b.not_filed_setup - a.not_filed_setup)
    .slice(page4 * pageSize, (page4 + 1) * pageSize);

  useEffect(() => {
    if (license_key) {
      dispatch(fetchTableOne({ liverun, license_key }));
    }
  }, [liverun, license_key, dispatch]);

  useEffect(() => {
    //select the first bar
    if (tableOne.length > 0 && license_key && !selectedDate) {
      const firstDate = tableOne[0].date;
      const { start_date, end_date } = tableOne[0];
      dispatch(setSelectedDate(firstDate));
      dispatch(fetchTableTwo({ start_date, end_date, liverun, license_key }));
      dispatch(fetchTableThree({ start_date, end_date, liverun, license_key }));
      setPage(0);
      setPage3(0);
      setPage4(0);
    }
  }, [tableOne, license_key, liverun, dispatch, selectedDate]);

  const handleChartClick = (data) => {
    const dateTitle = data?.activeLabel;
    if (!dateTitle) return;
    const matchedRecord = tableOne.find((entry) => entry.date === dateTitle);
    if (!matchedRecord) return;
    const { start_date, end_date } = matchedRecord;
    dispatch(setSelectedDate(dateTitle));
    dispatch(fetchTableTwo({ start_date, end_date, liverun, license_key }));
    dispatch(fetchTableThree({ start_date, end_date, liverun, license_key }));
    setPage(0);
    setPage3(0);
    setPage4(0);
  };

  const handleRowSelect = (params) => {
    const dateTitle = params.row?.output_date;
    dispatch(setSelectedDay(dateTitle));
    dispatch(fetchDayByCategory({ date: dateTitle, liverun, license_key }));
    dispatch(fetchDayData({ date: dateTitle, liverun, license_key }));
  };

  const chartFont = { fontSize: 11 };

  const pieData =
    selectedChartType === "filed"
      ? paginatedData
      : selectedChartType === "not_filed_inconclusive"
        ? paginatedData3
        : paginatedData4;

  const renderLegend = ({ payload }) => {
    const sorted = [...payload].sort(
      (a, b) => b.payload[selectedChartType] - a.payload[selectedChartType],
    );

    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12 }}>
        {sorted.map((entry, index) => (
          <li
            key={`item-${index}`}
            style={{ display: "flex", alignItems: "center", marginBottom: 4 }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: entry.color,
                marginRight: 8,
              }}
            />
            <span style={{ color: entry.color }}>
              {entry.payload.category} : {entry.payload[selectedChartType]}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const handleOpenFiling = (params) => {
    const lab_report_id = params.row?.lab_report_id;
    dispatch(setLabReport(lab_report_id));
    dispatch(fetchFiling({ lab_report_id, liverun, license_key }));
    setOpenFilingDialog(true);
  };

  const handleCloseFilingDialog = () => {
    setOpenFilingDialog(false);
  };

  return (
    <Box sx={{ mt: 1, pt: 0, pb: 0, px: 2 }}>
      <Paper elevation={2} sx={{ mt: 1, p: 2 }}>
        {/* Top Controls */}

        <Box
          sx={{
            background: "#F0F8FF",
            borderRadius: 3,
            p: 1.5,
          }}
        >
          <Grid container spacing={2.5} justifyContent="space-between">
            {[
              {
                value: tableOneTotalNormal,
                label1: "Total Pathology Normal",
                label2: "Reports Filed",
                color: "#faf5ff",
              },
              {
                value: tableOneTotalAbnormal,
                label1: "Total Pathology Abnormal",
                label2: "Reports Filed",
                color: "#fff7e6",
              },
              {
                value:
                  typeof tableOneTotalProcessed === "number"
                    ? Math.round((tableOneTotalProcessed / (60 * 24)) * 2) / 2
                    : 0,
                label1: "Days Saved",
                label2: "",
                label3: "(based on 1min per filing)",
                caption: true,
                color: "#f7faf7",
              },
              {
                value: tableOneEnabledCategories,
                label1: "Total Pathology",
                label2: "Categories Enabled",
                color: "#fff5f5",
              },
              {
                value: tableOneInreviewCategories,
                label1: "Total Pathology",
                label2: "Categories In Review",
                color: "#f9fafb",
              },
            ].map((card, idx) => (
              <Grid item xs={12} sm={6} md={2} key={idx}>
                <Paper
                  elevation={1}
                  sx={{
                    background: card.color,
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    height: "50%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    border: "1px solid #e2e8f0",
                    transition: "0.2s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      color: "#2d3748",
                      mb: 1,
                    }}
                  >
                    {typeof card.value === "number"
                      ? card.value.toLocaleString()
                      : card.value}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#4a5568",
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {card.label1}
                    <br />
                    {card.label2}
                  </Typography>

                  {card.caption && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, color: "#718096" }}
                    >
                      {card.label3}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}

            {/* ---------------- MONTHLY TREND CARD ---------------- */}
            <Grid item xs={12} sm={6} md={2}>
              <Paper
                elevation={1}
                sx={{
                  background: "#ffffff",
                  borderRadius: 2,
                  p: 2,
                  height: "75%",
                  border: "1px solid #e2e8f0",
                  transition: "0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#2d3748",
                    fontWeight: 600,
                    textAlign: "center",
                    mb: 1.5,
                  }}
                >
                  Monthly Trend
                </Typography>

                {Array.isArray(tableOneMonthlyTrend) &&
                  tableOneMonthlyTrend.map((item, idx) => {
                    const isPositive = item.percent_change_from_prev > 0;
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                          "&:last-child": { mb: 0 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "#4a5568", fontWeight: 600 }}
                        >
                          {item.month}:
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: "#2d3748", fontWeight: 500 }}
                        >
                          {item.filed}
                        </Typography>

                        <Box
                          sx={{
                            px: 1,
                            py: 0.2,
                            borderRadius: 1,
                            backgroundColor: isPositive ? "#c6f6d5" : "#fed7d7",
                            color: isPositive ? "#2f855a" : "#c53030",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                          }}
                        >
                          {isPositive ? (
                            <TrendingUp size={14} />
                          ) : (
                            <TrendingDown size={14} />
                          )}

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                            }}
                          >
                            {Math.abs(item.percent_change_from_prev).toFixed(1)}
                            %
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* 
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Activity - {liverun ? "Live Data" : "Dry Run Data"}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={liverun}
                onChange={() => dispatch(toggleLiverun())}
              />
            }
            label="Live / Dry Run"
          />
        </Box>
	   */}

        {/* Color Legend */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {Object.entries(COLORS).map(
            ([key, color]) =>
              (key !== "error" || is_admin) && (
                <FormControlLabel
                  key={key}
                  control={
                    <Switch
                      size="small"
                      checked={visibleKeys[key]}
                      onChange={() =>
                        setVisibleKeys((prev) => ({
                          ...prev,
                          [key]: !prev[key],
                        }))
                      }
                      sx={{
                        color,
                        "&.Mui-checked": { color },
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ color }}>
                      {COLOR_TITLES[key]}
                    </Typography>
                  }
                />
              ),
          )}
        </Box>

        {/* Charts Side-by-Side */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          {/* Left Column: Date-wise Filings */}
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle1">Weekly Filings</Typography>
            </Box>
            {loadingOne ? (
              <CircularProgress />
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={tableOne}
                  margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
                  onClick={handleChartClick}
                >
                  <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    style={chartFont}
                    interval={0}
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis style={chartFont} />
                  <Tooltip contentStyle={chartFont} />
                  <Legend wrapperStyle={chartFont} />
                  {visibleKeys.filed && (
                    <Bar
                      dataKey="filed"
                      name="Filed"
                      stackId="a"
                      fill={COLORS.filed}
                      stroke={BCOLORS.filed}
                      strokeWidth={1}
                    >
                      <LabelList dataKey="filed" position="top" fontSize={11} />
                    </Bar>
                  )}
                  {visibleKeys.reviewed_and_actioned && (
                    <Bar
                      dataKey="reviewed_and_actioned"
                      name="Not Filed (Inconclusive Report)"
                      stackId="a"
                      fill={COLORS.reviewed_and_actioned}
                      stroke={BCOLORS.reviewed_and_actioned}
                      strokeWidth={1}
                    >
                      <LabelList
                        dataKey="reviewed_and_actioned"
                        position="top"
                        fontSize={11}
                      />
                    </Bar>
                  )}
                  {visibleKeys.not_filed_inconclusive && (
                    <Bar
                      dataKey="not_filed_inconclusive"
                      name="Not Filed (Inconclusive Report)"
                      stackId="a"
                      fill={COLORS.not_filed_inconclusive}
                      stroke={BCOLORS.not_filed_inconclusive}
                      strokeWidth={1}
                    >
                      <LabelList
                        dataKey="not_filed_inconclusive"
                        position="top"
                        fontSize={11}
                      />
                    </Bar>
                  )}
                  {visibleKeys.not_filed_setup && (
                    <Bar
                      dataKey="not_filed_setup"
                      name="Category Not Setup"
                      stackId="a"
                      fill={COLORS.not_filed_setup}
                      stroke={BCOLORS.not_filed_setup}
                      strokeWidth={1}
                    >
                      <LabelList
                        dataKey="not_filed_setup"
                        position="top"
                        fontSize={11}
                      />
                    </Bar>
                  )}
                  {visibleKeys.not_filed_already && (
                    <Bar
                      dataKey="not_filed_already"
                      name="Not Filed (Already filed by clinician)"
                      stackId="a"
                      fill={COLORS.not_filed_already}
                      stroke={BCOLORS.not_filed_already}
                      strokeWidth={1}
                    >
                      <LabelList
                        dataKey="not_filed_already"
                        position="top"
                        fontSize={11}
                      />
                    </Bar>
                  )}
                  {visibleKeys.error && is_admin && (
                    <Bar
                      dataKey="error"
                      name="Error"
                      stackId="a"
                      fill={COLORS.error}
                      stroke={BCOLORS.error}
                      strokeWidth={1}
                    >
                      <LabelList dataKey="error" position="top" fontSize={11} />
                    </Bar>
                  )}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>

          {/* Right Column: Category Breakdown */}
          {selectedDate && !loadingTwo && (
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    mr: 1,
                    whiteSpace: "nowrap",
                    minWidth: 100,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: "bold",
                  }}
                >
                  {selectedDate}
                </Typography>
                <FormControl component="fieldset" sx={{ ml: 0.5 }}>
                  <RadioGroup
                    row
                    value={selectedChartType}
                    onChange={(e) => {
                      setSelectedChartType(e.target.value);
                      setPage(0);
                    }}
                  >
                    <FormControlLabel
                      value="filed"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">Filed</Typography>}
                    />
                    <FormControlLabel
                      value="not_filed_inconclusive"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Not Filed (Inconclusive Report)
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="not_filed_setup"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Category Not Setup
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Tooltip contentStyle={chartFont} />
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="left"
                    content={renderLegend}
                    wrapperStyle={{
                      ...chartFont,
                      lineHeight: "24px",
                      position: "absolute",
                      left: 0,
                      top: 0,
                    }}
                  />
                  <Pie
                    data={pieData}
                    dataKey={selectedChartType}
                    nameKey="category"
                    outerRadius={100}
                    innerRadius={60}
                    paddingAngle={2}
                    label
                    stroke="#fff"
                    strokeWidth={1}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                    mt: -5,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      selectedChartType === "filed"
                        ? page === 0
                        : selectedChartType === "not_filed_inconclusive"
                          ? page3 === 0
                          : page4 === 0
                    }
                    onClick={() => {
                      selectedChartType === "filed"
                        ? setPage((p) => p - 1)
                        : selectedChartType === "not_filed_inconclusive"
                          ? setPage3((p) => p - 1)
                          : setPage4((p) => p - 1);
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      selectedChartType === "filed"
                        ? page >= totalPages - 1
                        : selectedChartType === "not_filed_inconclusive"
                          ? page3 >= totalPages - 1
                          : page4 >= totalPages - 1
                    }
                    onClick={() => {
                      selectedChartType === "filed"
                        ? setPage((p) => p + 1)
                        : selectedChartType === "not_filed_inconclusive"
                          ? setPage3((p) => p + 1)
                          : setPage4((p) => p + 1);
                    }}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mt: 3 }}>
          {selectedDate && !loadingThree && (
            <DataGrid
              rowHeight={25}
              rows={tableThree}
              columns={TABLE_THREE_COLUMNS}
              pagination={false}
              hideFooter={true}
              onRowClick={handleRowSelect}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
              }
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  marginBottom: "20px",
                },
              }}
            />
          )}
        </Box>

        {selectedDay && (
          <>
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Category Data for {selectedDay}
              </Typography>
              {loadingDayByCategory ? (
                <CircularProgress />
              ) : (
                <DataGrid
                  rowHeight={25}
                  rows={tableDayByCategory}
                  columns={TABLE_CATEGORY_COLUMNS}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                  }
                />
              )}
            </Box>

            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Day Data for {selectedDay}
              </Typography>
              {loadingDayData ? (
                <CircularProgress />
              ) : (
                <DataGrid
                  rowHeight={25}
                  rows={tableDayData}
                  columns={TABLE_DAYDATA_COLUMNS}
                  pageSize={5}
                  onRowClick={handleOpenFiling}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                  }
                />
              )}
            </Box>
          </>
        )}

        <Dialog
          open={openFilingDialog}
          onClose={handleCloseFilingDialog}
          maxWidth="xl"
          fullWidth
          scroll="paper"
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            Filing Details
            <Button
              onClick={handleCloseFilingDialog}
              sx={{ float: "right" }}
              color="error"
              variant="outlined"
            >
              Close
            </Button>
          </DialogTitle>

          <DialogContent dividers>
            {loadingLabReport ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading filing...</Typography>
              </Box>
            ) : lab && Object.keys(lab).length > 0 ? (
              <>
                <LabFilingDetails lab={lab} />
              </>
            ) : null}
          </DialogContent>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default DashboardFrame;
